import { useRef, useState, useEffect, useCallback } from 'react';
import { useInView } from 'framer-motion';
import GrainOverlay from '../../../GrainOverlay';
import './MetricCards.css';

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

function CountUp({ end, duration = 2000, prefix = '', suffix = '' }) {
    const [display, setDisplay] = useState('0');
    const [started, setStarted] = useState(false);

    const start = useCallback(() => {
        if (started) return;
        setStarted(true);
        const startTime = performance.now();
        const isFloat = String(end).includes('.');
        const decimals = isFloat ? (String(end).split('.')[1] || '').length : 0;

        function tick(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);
            const current = eased * end;

            setDisplay(isFloat ? current.toFixed(decimals) : Math.round(current).toString());

            if (progress < 1) {
                requestAnimationFrame(tick);
            }
        }
        requestAnimationFrame(tick);
    }, [end, duration, started]);

    return { display: `${prefix}${display}${suffix}`, start };
}

function MetricCard({ metric, index, isInView }) {
    const { display, start } = CountUp({
        end: metric.value || 0,
        duration: metric.duration || 2000,
        prefix: metric.prefix || '',
        suffix: metric.suffix || '',
    });

    useEffect(() => {
        if (isInView && !metric.symbol) {
            const timer = setTimeout(start, index * 150);
            return () => clearTimeout(timer);
        }
    }, [isInView, start, index, metric.symbol]);

    const isSymbol = !!metric.symbol;

    return (
        <div
            className={`MetricCard grain-light ${isSymbol ? 'MetricCard--symbol' : ''}`}
            style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s`,
            }}
        >
            <GrainOverlay intensity={0.05} />
            <span className="MetricCard-number">{isSymbol ? metric.symbol : display}</span>
            <span className="MetricCard-label" dangerouslySetInnerHTML={{ __html: metric.label }} />
        </div>
    );
}

const MetricCards = ({ metrics, title }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });

    return (
        <div className="MetricCards" ref={ref}>
            {title && <h2 className="MetricCards-title">{title}</h2>}
            <div className="MetricCards-grid">
                {metrics.map((metric, i) => (
                    <MetricCard key={i} metric={metric} index={i} isInView={isInView} />
                ))}
            </div>
        </div>
    );
};

export default MetricCards;
